import { XML_LANGUAGE_ID } from '@lved/shared-core';
import { MonacoLanguageDefinition } from '@theia-lved-core/types/monaco-language';
import { FrontendApplicationContribution } from '@theia/core/lib/browser';
import { DisposableCollection } from '@theia/core/lib/common/disposable';
import { injectable } from '@theia/core/shared/inversify';
import * as monaco from '@theia/monaco-editor-core';

import { cmakeLanguageDefinition } from './languages/cmake';
import { cLanguageDefinition, cppLanguageDefinition } from './languages/cpp';
import { jsonLanguageDefinition } from './languages/json';
import { markdownLanguageDefinition } from './languages/markdown';
import { xmlLanguageDefinition } from './languages/xml';
import { yamlLanguageDefinition } from './languages/yaml';

@injectable()
export class MonacoSyntaxHighlightingContribution implements FrontendApplicationContribution {
    protected readonly toDispose: DisposableCollection = new DisposableCollection();

    initialize(): void {
        const definitions: readonly MonacoLanguageDefinition[] = [
            cLanguageDefinition,
            cppLanguageDefinition,
            jsonLanguageDefinition,
            xmlLanguageDefinition,
            markdownLanguageDefinition,
            yamlLanguageDefinition,
            cmakeLanguageDefinition
        ];

        for (const definition of definitions) {
            this.registerLanguage(definition);
        }

        this.toDispose.push(
            monaco.editor.onDidCreateModel((model: monaco.editor.ITextModel) => this.ensureXmlLanguage(model))
        );

        for (const model of monaco.editor.getModels()) {
            this.ensureXmlLanguage(model);
        }
    }

    onStop(): void {
        this.toDispose.dispose();
    }

    protected registerLanguage(definition: MonacoLanguageDefinition): void {
        if (!monaco.languages.getLanguages().some((language) => language.id === definition.id)) {
            monaco.languages.register({
                id: definition.id,
                aliases: definition.aliases,
                extensions: definition.extensions,
                filenames: definition.filenames
            });
        }

        monaco.languages.setLanguageConfiguration(definition.id, definition.configuration);
        monaco.languages.setMonarchTokensProvider(definition.id, definition.language);
    }

    protected ensureXmlLanguage(model: monaco.editor.ITextModel): void {
        if (!model.uri.path.toLowerCase().endsWith('.xml')) return;
        if (model.getLanguageId() === XML_LANGUAGE_ID) return;

        monaco.editor.setModelLanguage(model, XML_LANGUAGE_ID);
    }
}
